import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";

interface Partner {
  id: string;
  name: string;
  email: string;
}

interface SentEmail {
  id: string;
  sent_by: string;
  sent_at: string;
  subject: string;
  body: string;
  recipients: string[];
  failed: string[];
  attachments: { name: string; size: number }[];
}

// Matches EMAIL_ATTACHMENTS_MAX_MB on the backend.
const MAX_MB = 18;
const MAX_FILES = 10;

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  if (e?.response?.status === 413) return `Attachments must be ${MAX_MB} MB or smaller in total.`;
  return "Request failed. Please try again.";
}

const size = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const when = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export default function EmailManager() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [mode, setMode] = useState<"all" | "some">("all");
  const [picked, setPicked] = useState<string[]>([]);
  const [extra, setExtra] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sent, setSent] = useState<SentEmail[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<SentEmail[]>("/emails");
      setSent(data);
    } catch (e) {
      setSent([]);
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    load();
    api.get<Partner[]>("/partners")
      .then(({ data }) => setPartners(data.filter((p) => p.email).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setPartners([]));
  }, [load]);

  const partnerEmails = mode === "all" ? partners.map((p) => p.email.toLowerCase()) : picked;
  const unpicked = partners.filter((p) => !picked.includes(p.email.toLowerCase()));
  const nameOf = (email: string) => partners.find((p) => p.email.toLowerCase() === email)?.name ?? email;

  const typed = extra.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
  const recipients = Array.from(new Set([...partnerEmails, ...typed]));
  const totalSize = files.reduce((n, f) => n + f.size, 0);

  const addFiles = (list: FileList | null) => {
    setError(null);
    const next = [...files, ...Array.from(list ?? [])];
    if (fileInput.current) fileInput.current.value = "";
    if (next.length > MAX_FILES) return setError(`At most ${MAX_FILES} attachments per email.`);
    if (next.reduce((n, f) => n + f.size, 0) > MAX_MB * 1024 * 1024) return setError(`Attachments must be ${MAX_MB} MB or smaller in total.`);
    setFiles(next);
  };

  const sendEmail = async () => {
    setError(null);
    setNotice(null);
    if (!recipients.length) return setError("Choose at least one recipient.");
    if (!subject.trim()) return setError("Please enter a subject.");
    const ok = window.confirm(
      `Send "${subject.trim()}" to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}` +
      (files.length ? ` with ${files.length} attachment${files.length === 1 ? "" : "s"}` : "") + "?\n\nEach recipient gets their own copy.",
    );
    if (!ok) return;

    const form = new FormData();
    form.append("recipients", recipients.join(","));
    form.append("subject", subject);
    form.append("body", body);
    files.forEach((f) => form.append("attachments", f, f.name));
    setBusy(true);
    try {
      const { data } = await api.post<SentEmail>("/emails", form);
      setNotice(`Sent "${data.subject}" to ${data.recipients.length} recipient${data.recipients.length === 1 ? "" : "s"}.` +
        (data.failed.length ? ` Not delivered to: ${data.failed.join(", ")}.` : ""));
      setMode("all"); setPicked([]); setExtra(""); setSubject(""); setBody(""); setFiles([]);
      load();
    } catch (e) {
      setError(errorText(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={S.card}>
        <h3 style={{ ...S.title, marginBottom: 16 }}>Send email</h3>
        {notice && <div style={S.success}>{notice}</div>}
        {error && <pre style={S.error}>{error}</pre>}
        <div style={S.form}>
          <div style={{ ...S.field, gridColumn: "1 / -1" }}>
            <span style={S.label}>Send to</span>
            <div style={S.chips}>
              <label style={S.radio}>
                <input type="radio" name="send-to" checked={mode === "all"} onChange={() => setMode("all")} />
                All partners ({partners.length})
              </label>
              <label style={S.radio}>
                <input type="radio" name="send-to" checked={mode === "some"} onChange={() => setMode("some")} />
                Specific partners
              </label>
            </div>
            {mode === "some" && (
              <>
                <select style={S.input} value="" disabled={!unpicked.length}
                  onChange={(e) => e.target.value && setPicked([...picked, e.target.value])}>
                  <option value="">{unpicked.length ? "Choose a partner to add" : "All partners added"}</option>
                  {unpicked.map((p) => <option key={p.id} value={p.email.toLowerCase()}>{p.name} ({p.email})</option>)}
                </select>
                {picked.length > 0 ? (
                  <div style={S.chips}>
                    {picked.map((email) => (
                      <span key={email} style={{ ...S.chip, ...S.chipOn }} title={email}>
                        {nameOf(email)}
                        <button style={S.chipX} aria-label={`Remove ${nameOf(email)}`}
                          onClick={() => setPicked(picked.filter((e) => e !== email))}>×</button>
                      </span>
                    ))}
                  </div>
                ) : <span style={S.sub}>No partners chosen yet.</span>}
              </>
            )}
          </div>
          <label style={{ ...S.field, gridColumn: "1 / -1" }}>
            <span style={S.label}>Other addresses (optional)</span>
            <input style={S.input} placeholder="name@example.com, another@example.com" value={extra}
              onChange={(e) => setExtra(e.target.value)} />
          </label>
          <label style={{ ...S.field, gridColumn: "1 / -1" }}>
            <span style={S.label}>Subject</span>
            <input style={S.input} maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label style={{ ...S.field, gridColumn: "1 / -1" }}>
            <span style={S.label}>Message</span>
            <textarea style={{ ...S.input, minHeight: 160, resize: "vertical", fontFamily: "inherit" }} maxLength={20000}
              value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
          <div style={{ ...S.field, gridColumn: "1 / -1" }}>
            <span style={S.label}>Attachments (optional, up to {MAX_FILES} files, {MAX_MB} MB in total)</span>
            <input ref={fileInput} style={{ ...S.input, padding: "7px 10px" }} type="file" multiple
              onChange={(e) => addFiles(e.target.files)} />
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} style={S.fileRow}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                <span style={S.sub}>{size(f.size)}</span>
                <button style={{ ...S.rowBtn, ...S.deleteBtn }} onClick={() => setFiles(files.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            {files.length > 1 && <span style={S.sub}>Total {size(totalSize)}</span>}
          </div>
          <div style={S.formActions}>
            <button style={{ ...S.primaryBtn, ...(busy ? S.disabled : {}) }} disabled={busy} onClick={sendEmail}>
              {busy ? "Sending…" : `Send to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`}
            </button>
          </div>
          <p style={S.hint}>Sent from the fund's email account (the one used for the monthly report). Each recipient gets a separate copy, so nobody sees the other addresses.</p>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.header}>
          <h3 style={S.title}>Sent emails {sent && <span style={S.count}>({sent.length})</span>}</h3>
        </div>
        {sent === null ? <Spinner /> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={S.table}>
              <thead>
                <tr>{["Sent", "Subject", "Recipients", "Attachments", "By"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {sent.map((m) => (
                  <Fragment key={m.id}>
                    <tr style={{ cursor: "pointer" }} onClick={() => setOpen(open === m.id ? null : m.id)}>
                      <td style={{ ...S.td, color: "var(--text-secondary)" }}>{when(m.sent_at)}</td>
                      <td style={{ ...S.td, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{m.subject}</td>
                      <td style={S.td}>
                        {m.recipients.length}
                        {m.failed.length > 0 && <span style={{ color: "#f08a8a" }}> · {m.failed.length} failed</span>}
                      </td>
                      <td style={S.td}>{m.attachments.length || "-"}</td>
                      <td style={{ ...S.td, color: "var(--text-secondary)" }}>{m.sent_by}</td>
                    </tr>
                    {open === m.id && (
                      <tr>
                        <td style={{ ...S.td, whiteSpace: "pre-wrap" }} colSpan={5}>
                          <div style={S.sub}>To: {m.recipients.join(", ") || "-"}</div>
                          {m.failed.length > 0 && <div style={{ ...S.sub, color: "#f08a8a" }}>Not delivered: {m.failed.join(", ")}</div>}
                          {m.attachments.length > 0 && <div style={S.sub}>Attachments: {m.attachments.map((a) => `${a.name} (${size(a.size)})`).join(", ")}</div>}
                          <div style={{ marginTop: 8 }}>{m.body || <span style={S.sub}>(no message)</span>}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {sent.length === 0 && (
                  <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={5}>No emails sent yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  form: {
    display: "grid", gap: 16, marginBottom: 20, padding: 16, borderRadius: 8,
    background: "rgba(255,255,255,0.03)",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14, width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "not-allowed" },
  formActions: { display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" },
  hint: { gridColumn: "1 / -1", fontSize: 12, color: "var(--text-muted)" },
  primaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  secondaryBtn: {
    padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
  sub: { fontSize: 12, color: "var(--text-muted)" },
  chips: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  chip: {
    padding: "6px 12px", borderRadius: 999, fontSize: 13,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)",
  },
  chipX: { marginLeft: 8, color: "inherit", fontSize: 14, lineHeight: 1 },
  radio: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--text-primary)", cursor: "pointer" },
  chipOn: { background: "rgba(29,158,117,0.18)", borderColor: "var(--teal)", color: "var(--teal-light)" },
  fileRow: { display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "var(--text-primary)" },
};
